import React, {useCallback, useEffect, useMemo} from "react";
import PropTypes from "prop-types";
import {useTheme} from "../../theme/ThemeProvider";
import BaseChart from "./BaseChart";

/**
 * BarChart component that renders bar series with tooltips using Canvas
 *
 * @param {Object} props
 * @param {Array} props.data - The dataset with x and y values for each series
 * @param {Array} props.series - Array of series configurations (color, name, etc.)
 * @param {Object} props.settings - Chart settings and appearance options
 */
const BarChart = ({data = [], series = [], settings = {}, ...rest}) => {
  const chartSettings = useMemo(() => ({...settings}), [settings]);

  const BarChartRenderer = ({
    canvasRef,
    containerRef,
    chartSettings,
    hovering,
    setTooltipData,
    setTooltipPosition,
  }) => {
    const {theme} = useTheme();

    const drawChart = useCallback(() => {
      if (!canvasRef.current || !containerRef.current || data.length === 0)
        return;

      const containerWidth = containerRef.current.clientWidth || 0;
      const containerHeight = chartSettings.height || 0;

      if (containerWidth <= 0 || containerHeight <= 0) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d", {alpha: true});

      const dpr = window.devicePixelRatio || 1;
      canvas.width = containerWidth * dpr;
      canvas.height = containerHeight * dpr;

      ctx.scale(dpr, dpr);
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;

      ctx.clearRect(0, 0, containerWidth, containerHeight);

      const padding = chartSettings.padding;
      const chartWidth = containerWidth - padding.left - padding.right;
      const chartHeight = containerHeight - padding.top - padding.bottom;

      let yMax = 0;
      data.forEach((item) => {
        series.forEach((s) => {
          yMax = Math.max(yMax, item[s.dataKey] || 0);
        });
      });

      yMax = yMax * 1.2;

      const barWidth = chartWidth / data.length / (series.length + 0.5);

      const xScale = (index, seriesIndex) => {
        const groupWidth = barWidth * series.length;
        const groupStart =
          padding.left +
          (index * chartWidth) / data.length +
          (chartWidth / data.length - groupWidth) / 2;
        return groupStart + seriesIndex * barWidth;
      };

      const yScale = (value) =>
        padding.top + chartHeight - (value / yMax) * chartHeight;

      if (chartSettings.grid.horizontal) {
        ctx.strokeStyle = chartSettings.grid.color || theme.colors.gray200;
        ctx.lineWidth = 1;
        ctx.setLineDash(chartSettings.grid.dashArray);

        const yTicks = chartSettings.axis.y.tickCount;
        for (let i = 0; i < yTicks; i++) {
          const y = padding.top + (i / (yTicks - 1)) * chartHeight;
          ctx.beginPath();
          ctx.moveTo(padding.left, y);
          ctx.lineTo(containerWidth - padding.right, y);
          ctx.stroke();
        }
      }

      if (chartSettings.axis.y.visible) {
        ctx.font = `${chartSettings.axis.y.labelFontSize}px sans-serif`;
        ctx.fillStyle = chartSettings.axis.y.tickColor || theme.colors.gray700;
        ctx.textAlign = "right";

        if (
          chartSettings.axis.y.customTicks &&
          chartSettings.axis.y.customTicks.length > 0
        ) {
          const customTicks = chartSettings.axis.y.customTicks;

          const maxTickValue = Math.max(...customTicks);

          customTicks.forEach((value) => {
            const normalizedValue = value / maxTickValue;
            const y = padding.top + chartHeight - normalizedValue * chartHeight;

            if (chartSettings.grid.horizontal) {
              ctx.strokeStyle =
                chartSettings.grid.color || theme.colors.gray200;
              ctx.setLineDash(chartSettings.grid.dashArray);
              ctx.beginPath();
              ctx.moveTo(padding.left, y);
              ctx.lineTo(containerWidth - padding.right, y);
              ctx.stroke();
            }

            ctx.fillText(value.toString(), padding.left - 10, y + 4);
          });
        } else {
          const yTicks = chartSettings.axis.y.tickCount;
          for (let i = 0; i < yTicks; i++) {
            const y = padding.top + (i / (yTicks - 1)) * chartHeight;
            const value = yMax - (i / (yTicks - 1)) * yMax;
            ctx.fillText(
              Math.round(value).toString(),
              padding.left - 10,
              y + 4,
            );
          }
        }
      }

      if (chartSettings.axis.x.visible) {
        ctx.strokeStyle =
          chartSettings.axis.x.tickColor || theme.colors.gray300;
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(padding.left, padding.top + chartHeight);
        ctx.lineTo(containerWidth - padding.right, padding.top + chartHeight);
        ctx.stroke();

        ctx.font = `${chartSettings.axis.x.labelFontSize}px sans-serif`;
        ctx.fillStyle = chartSettings.axis.x.tickColor || theme.colors.gray700;
        ctx.textAlign = "center";

        const labelInterval = Math.max(
          1,
          Math.ceil(data.length / chartSettings.axis.x.tickCount),
        );
        for (let i = 0; i < data.length; i += labelInterval) {
          if (i < data.length) {
            const x = padding.left + (i + 0.5) * (chartWidth / data.length);

            const labelText =
              data[i].displayX !== undefined ? data[i].displayX : data[i].x;
            ctx.fillText(labelText, x, containerHeight - padding.bottom / 3);
          }
        }
      }

      data.forEach((item, i) => {
        const dataPointCenterX =
          padding.left + (i + 0.5) * (chartWidth / data.length);

        if (chartSettings.labels && chartSettings.labels.top) {
          const topConfig = chartSettings.labels.top;

          let totalValue = 0;

          if (topConfig.showTotal) {
            series.forEach((s) => {
              totalValue += item[s.dataKey] || 0;
            });
          } else {
            totalValue = topConfig.dataKey ? item[topConfig.dataKey] || 0 : 0;
          }

          const formattedValue = topConfig.formatter
            ? topConfig.formatter(totalValue)
            : totalValue.toString();

          ctx.font = `${topConfig.fontSize || 12}px ${topConfig.fontFamily || "sans-serif"}`;
          ctx.fillStyle = topConfig.textColor || theme.colors.gray700;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";

          const highestValue = Math.max(
            ...series.map((s) => item[s.dataKey] || 0),
          );
          const labelY = yScale(highestValue) - (topConfig.marginY || 8);

          ctx.fillText(formattedValue, dataPointCenterX, labelY);
        }

        series.forEach((s, seriesIndex) => {
          const value = item[s.dataKey] || 0;

          if (value === 0) return;

          const barHeight = (value / yMax) * chartHeight;

          const minHeight = chartSettings.minBarHeight || 10;
          const actualBarHeight = Math.max(barHeight, minHeight);

          const x = xScale(i, seriesIndex);
          const y = yScale(value) - (actualBarHeight - barHeight);

          if (
            chartSettings.customBarRenderer &&
            typeof chartSettings.customBarRenderer === "function"
          ) {
            chartSettings.customBarRenderer(
              ctx,
              x,
              y,
              barWidth - 2,
              actualBarHeight,
              item,
              seriesIndex,
            );
          } else {
            ctx.fillStyle = item.color || s.color;
            ctx.beginPath();

            const radius = theme.radius.md || 4;
            const barX = x;
            const barY = y;
            const barW = barWidth - 2;
            const barH = actualBarHeight;

            ctx.moveTo(barX + radius, barY);
            ctx.lineTo(barX + barW - radius, barY);
            ctx.quadraticCurveTo(barX + barW, barY, barX + barW, barY + radius);
            ctx.lineTo(barX + barW, barY + barH - radius);
            ctx.quadraticCurveTo(
              barX + barW,
              barY + barH,
              barX + barW - radius,
              barY + barH,
            );
            ctx.lineTo(barX + radius, barY + barH);
            ctx.quadraticCurveTo(barX, barY + barH, barX, barY + barH - radius);
            ctx.lineTo(barX, barY + radius);
            ctx.quadraticCurveTo(barX, barY, barX + radius, barY);
            ctx.closePath();

            ctx.fill();
          }
        });

        if (chartSettings.labels && chartSettings.labels.bottom) {
          const bottomConfig = chartSettings.labels.bottom;

          const labelX = dataPointCenterX;
          const labelY =
            containerHeight - (bottomConfig.marginY || padding.bottom / 2);

          if (
            bottomConfig.renderer &&
            typeof bottomConfig.renderer === "function"
          ) {
            bottomConfig.renderer(ctx, item, labelX, labelY, theme);
          } else {
            if (bottomConfig.labelKey && item[bottomConfig.labelKey]) {
              const labelText = item[bottomConfig.labelKey];
              ctx.font = `${bottomConfig.labelFontSize || 12}px ${bottomConfig.fontFamily || "sans-serif"}`;
              ctx.fillStyle = bottomConfig.labelColor || theme.colors.gray600;
              ctx.textAlign = "center";
              ctx.textBaseline = "top";
              ctx.fillText(
                labelText,
                labelX,
                labelY + (bottomConfig.iconKey ? 25 : 0),
              );
            }
          }
        }
      });
    }, [canvasRef, containerRef, chartSettings, data, series, theme]);

    const handleMouseMove = useCallback(
      (event) => {
        if (!hovering || !canvasRef.current || !containerRef.current) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        const containerWidth = containerRef.current.clientWidth || 0;
        const containerHeight = chartSettings.height || 0;
        const padding = chartSettings.padding;
        const chartWidth = containerWidth - padding.left - padding.right;
        const chartHeight = containerHeight - padding.top - padding.bottom;

        if (
          mouseX < padding.left ||
          mouseX > containerWidth - padding.right ||
          mouseY < padding.top ||
          mouseY > containerHeight - padding.bottom
        ) {
          setTooltipData(null);
          return;
        }

        let yMax = 0;
        data.forEach((item) => {
          series.forEach((s) => {
            yMax = Math.max(yMax, item[s.dataKey] || 0);
          });
        });

        yMax = yMax * 1.2;

        const barWidth = chartWidth / data.length / (series.length + 0.5);

        const xScale = (index, seriesIndex) => {
          const groupWidth = barWidth * series.length;
          const groupStart =
            padding.left +
            (index * chartWidth) / data.length +
            (chartWidth / data.length - groupWidth) / 2;
          return groupStart + seriesIndex * barWidth;
        };

        const yScale = (value) =>
          padding.top + chartHeight - (value / yMax) * chartHeight;

        const yBottom = padding.top + chartHeight;

        let closestPointDistance = Infinity;
        let tooltipInfo = null;

        data.forEach((item, i) => {
          series.forEach((s, seriesIndex) => {
            const value = item[s.dataKey] || 0;

            if (value === 0) return;

            const barHeight = (value / yMax) * chartHeight;

            const minHeight = chartSettings.minBarHeight || 10;
            const actualBarHeight = Math.max(barHeight, minHeight);

            const barX = xScale(i, seriesIndex);
            const barY = yScale(value) - (actualBarHeight - barHeight);

            if (
              mouseX >= barX &&
              mouseX <= barX + barWidth - 2 &&
              mouseY >= barY &&
              mouseY <= barY + actualBarHeight
            ) {
              const distance = Math.abs(mouseX - (barX + barWidth / 2));
              if (distance < closestPointDistance) {
                closestPointDistance = distance;
                tooltipInfo = {
                  series: s,
                  dataPoint: item,
                  value,
                  x: barX + barWidth / 2,
                  y: barY,
                };
              }
            }
          });
        });

        if (tooltipInfo) {
          setTooltipData({
            items: [
              {
                label:
                  tooltipInfo.dataPoint.sentiment ||
                  tooltipInfo.series.name ||
                  "Unknown",
                value: `${tooltipInfo.value.toLocaleString()} ${tooltipInfo.dataPoint.sentiment ? "guests" : "items"}`,
                color: tooltipInfo.dataPoint.color || tooltipInfo.series.color,
              },
            ],
          });
          setTooltipPosition({x: tooltipInfo.x, y: tooltipInfo.y});
        } else {
          setTooltipData(null);
        }
      },
      [
        canvasRef,
        containerRef,
        chartSettings,
        data,
        series,
        hovering,
        setTooltipData,
        setTooltipPosition,
      ],
    );

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const boundMouseMove = (e) => handleMouseMove(e);
      canvas.addEventListener("mousemove", boundMouseMove);

      return () => {
        canvas.removeEventListener("mousemove", boundMouseMove);
      };
    }, [handleMouseMove]);

    useEffect(() => {
      if (canvasRef.current && containerRef.current && data.length > 0) {
        drawChart();
      }
    }, [drawChart]);

    useEffect(() => {
      const handleResize = () => {
        if (canvasRef.current && containerRef.current && data.length > 0) {
          drawChart();
        }
      };

      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, [drawChart, data.length]);

    return null;
  };

  return (
    <BaseChart
      data={data}
      series={series}
      settings={chartSettings}
      renderer={BarChartRenderer}
      {...rest}
    />
  );
};

BarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  series: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      dataKey: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ),
  settings: PropTypes.object,
};

export default BarChart;
